import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import type {
  LinkPaymentResult,
  PaymentsSummary,
  RecordPaymentResult,
  UnmatchedPayment,
} from '@repo/shared-types';
import type { RecordPaymentInput } from '@repo/validation';
import { fetchApi } from '../lib/api';

function invalidateAll(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: ['invoices'] });
  qc.invalidateQueries({ queryKey: ['bookkeeping'] });
}

// `['payments']` prefix invalidation also covers `['payments','unmatched']`
// and `['payments','summary']` — TanStack Query matches by key prefix.
function invalidatePayments(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: ['payments'] });
  qc.invalidateQueries({ queryKey: ['invoices'] });
}

// Ignoring a credit (or un-ignoring, via link) changes whether it counts as
// revenue — see reviewState: { not: 'IGNORED' } in bookkeepingQueries.ts — so
// bookkeeping figures must be invalidated alongside payments/invoices.
function invalidatePaymentsAndBookkeeping(qc: ReturnType<typeof useQueryClient>) {
  invalidatePayments(qc);
  qc.invalidateQueries({ queryKey: ['bookkeeping'] });
}

export function useRecordPayment() {
  const qc = useQueryClient();
  const { t } = useTranslation('payments');
  return useMutation<RecordPaymentResult, Error, RecordPaymentInput>({
    mutationFn: (input) =>
      fetchApi('/payments', {
        method: 'POST',
        body: JSON.stringify(input),
      }),
    onSuccess: () => {
      invalidateAll(qc);
    },
    onError: (err) => {
      toast.error(err.message || t('toast.error'));
    },
  });
}

// ── Unmatched-payments inbox ────────────────────────────────────────────────

export function useUnmatchedPayments() {
  return useQuery<UnmatchedPayment[]>({
    queryKey: ['payments', 'unmatched'],
    queryFn: () => fetchApi('/payments?state=OPEN'),
  });
}

export function usePaymentsSummary(enabled = true) {
  return useQuery<PaymentsSummary>({
    queryKey: ['payments', 'summary'],
    queryFn: () => fetchApi('/payments/summary'),
    enabled,
  });
}

export function useLinkPayment() {
  const qc = useQueryClient();
  const { t } = useTranslation('payments');
  return useMutation<LinkPaymentResult, Error, { id: string; invoiceId: string }>({
    mutationFn: ({ id, invoiceId }) =>
      fetchApi(`/payments/${id}/link`, {
        method: 'POST',
        body: JSON.stringify({ invoiceId }),
      }),
    onSuccess: () => {
      invalidatePayments(qc);
      toast.success(t('inbox.toast.linked'));
    },
    onError: (err) => {
      toast.error(err.message || t('toast.error'));
    },
  });
}

export function useIgnorePayment() {
  const qc = useQueryClient();
  const { t } = useTranslation('payments');
  return useMutation<{ id: string; reviewState: string }, Error, string>({
    mutationFn: (id) => fetchApi(`/payments/${id}/ignore`, { method: 'POST' }),
    onSuccess: () => {
      invalidatePaymentsAndBookkeeping(qc);
      toast.success(t('inbox.toast.ignored'));
    },
    onError: (err) => {
      toast.error(err.message || t('toast.error'));
    },
  });
}

export function useDeletePayment() {
  const qc = useQueryClient();
  const { t } = useTranslation('payments');
  return useMutation<{ deleted: boolean; invoiceStatus: string | null }, Error, string>({
    mutationFn: (id) => fetchApi(`/payments/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      invalidateAll(qc);
      toast.success(t('toast.deleted'));
    },
    onError: (err) => {
      toast.error(err.message || t('toast.error'));
    },
  });
}
